import { useState, useEffect, useRef } from "react";
import Avatar from "../../Images/Avatar.png";
import { BASEAPIURL, token } from "../../env";
export default function Members() {
  const [memberList, setMemberList] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]); // For search results
  const [selectedLetter, setSelectedLetter] = useState("A");
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [relationData, setRelationData] = useState([]);
  const [relationLoading, setRelationLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(""); // New: Search term

  const fetchMembers = async (page = pageNumber, letter = selectedLetter) => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(
        `${BASEAPIURL}/MemberListwithfilter?pageSize=28&pageNumber=${page}&startsWith=${letter}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tokenData: token }),
        }
      );
      if (!response.ok) throw new Error(`No Member Found`);
      const result = await response.json();
      console.log("Members API Response:", result);
      if (result.data) {
        const updatedMembers = result.data.map((member) => ({
          ...member,
          Occupations: Array.isArray(member.Occupations)
            ? member.Occupations
            : member.Occupations && typeof member.Occupations === "object"
            ? [member.Occupations]
            : [],
        }));
        setMemberList(updatedMembers);
        setFilteredMembers(updatedMembers); // Initialize filtered list
        setHasMore(updatedMembers.length === 28);
      } else {
        setMemberList([]);
        setFilteredMembers([]);
        setHasMore(false);
      }
    } catch (err) {
      console.error("Member fetch error:", err);
      setError(err.message || "Failed to fetch members");
      setMemberList([]);
      setFilteredMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembersAll = async (
    page = pageNumber,
    letter = selectedLetter
  ) => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(
        `${BASEAPIURL}/AllMemberListwithfilter?pageSize=28&pageNumber=${page}&startsWith=${letter}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tokenData: token }),
        }
      );
      if (!response.ok) throw new Error(`No Member Found`);
      const result = await response.json();
      console.log("Members API Response:", result);
      if (result.data) {
        const updatedMembers = result.data.map((member) => ({
          ...member,
          Occupations: Array.isArray(member.Occupations)
            ? member.Occupations
            : member.Occupations && typeof member.Occupations === "object"
            ? [member.Occupations]
            : [],
        }));
        setMemberList(updatedMembers);
        setFilteredMembers(updatedMembers);
        setHasMore(updatedMembers.length === 28);
      } else {
        setMemberList([]);
        setFilteredMembers([]);
        setHasMore(false);
      }
    } catch (err) {
      console.error("Member fetch error:", err);
      setError(err.message || "Failed to fetch members");
      setMemberList([]);
      setFilteredMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelations = async (memID) => {
    try {
      console.log("Fetching relations for MemID:", memID);
      setRelationLoading(true);
      const res = await fetch(
        `${BASEAPIURL}/DependentListByMemID?MemID=${memID}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tokenData: token }),
        }
      );
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      console.log("Relation API Response:", data);
      setRelationData(Array.isArray(data.data) ? data.data : []);
    } catch (error) {
      console.error("Error fetching relations:", error);
      setRelationData([]);
    } finally {
      setRelationLoading(false);
    }
  };

  useEffect(() => {
    if (selectedLetter) {
      fetchMembers(pageNumber, selectedLetter);
    }
  }, [selectedLetter]);

  const debounceTimerRef = useRef(null);

  useEffect(() => {
    if (searchTerm) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        (async () => {
          fetchMembersAll(pageNumber, searchTerm);
        })();
      }, 400);
    }
  }, [searchTerm]);

  useEffect(() => {
    if (!loading) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [loading, pageNumber]);

  const handleFilterSearch = (letter) => {
    if (letter) {
      setSearchTerm(letter);
      if (selectedLetter) {
        setSelectedLetter("");
        setPageNumber(1);
      }
    } else {
      setSearchTerm("");
      setSelectedLetter("A");
      setPageNumber(1);
    }
  };

  const handleFilterLetter = (letter) => {
    setSelectedLetter(letter);
    if (searchTerm) {
      setSearchTerm("");
      setPageNumber(1);
    }
  };

  const openModal = (member) => {
    setSelectedMember(member);
    fetchRelations(member.MemID);
  };

  const closeModal = () => {
    setSelectedMember(null);
    setRelationData([]);
  };

  const getImage = (member) => {
    if (member?.ImageBase64) {
      return member.ImageBase64.startsWith("data:")
        ? member.ImageBase64
        : `data:image/jpeg;base64,${member.ImageBase64}`;
    }
    return Avatar;
  };

  const getDependentImage = (imageName) => {
    if (!imageName) return Avatar;
    if (imageName.startsWith("data:")) return imageName;
    return `${BASEAPIURL}/MemberImages/${imageName}`;
  };

  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  return (
    <div className="container py-4">
      <h1 className="text-center mb-4">Club Members</h1>

      {/* Search Bar */}
      <div className="mb-3">
        <input
          type="text"
          className="form-control form-control-lg"
          placeholder="🔍 Search by member name..."
          value={searchTerm}
          onChange={(e) => handleFilterSearch(e.target.value)}
        />
      </div>

      {/* Alphabet Filter */}
      <div className="d-flex flex-wrap gap-2 mb-4 justify-content-center">
        {alphabet.map((char) => (
          <button
            key={char}
            className={`btn btn-sm ${
              selectedLetter === char ? "btn-primary" : "btn-outline-primary"
            }`}
            onClick={() => handleFilterLetter(char)}
          >
            {char}
          </button>
        ))}
      </div>

      {error && <div className="alert alert-danger text-center">{error}</div>}

      {/* Member Grid */}
      <div className="row g-4">
        {loading ? (
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : filteredMembers.length > 0 ? (
          filteredMembers.map((member) => (
            <div key={member.MemID} className="col-sm-6 col-md-4 col-lg-3">
              <div
                className="card p-3 text-center shadow-sm h-100"
                style={{ cursor: "pointer", minHeight: "320px" }}
                onClick={() => openModal(member)}
              >
                <img
                  src={getImage(member)}
                  alt={member.MemName}
                  className="rounded-circle mb-2"
                  style={{
                    width: 100,
                    height: 100,
                    objectFit: "cover",
                    border: "2px solid #007bff",
                    margin: "auto",
                  }}
                />
                <h5 className="h6">{member.MemName}</h5>
                <p className="text-muted small mb-1">{member.MemMobileNo}</p>
                <p className="text-muted small mb-1">{member.MemEmailID}</p>
                <button
                  className="btn btn-sm btn-outline-primary mt-auto"
                  onClick={(e) => {
                    e.stopPropagation();
                    openModal(member);
                  }}
                >
                  View Details
                </button>
              </div>
            </div>
          ))
        ) : (
          !loading && (
            <p className="text-center text-muted col-12">
              No members found matching "{searchTerm}".
            </p>
          )
        )}
      </div>

      {/* Pagination */}
      {!searchTerm && ( // Hide pagination when searching
        <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
          <button
            className="btn btn-outline-secondary"
            onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
            disabled={pageNumber === 1 || loading}
          >
            ← Previous
          </button>
          <span className="fw-bold">Page {pageNumber}</span>
          <button
            className="btn btn-outline-primary"
            onClick={() => setPageNumber((prev) => prev + 1)}
            disabled={!hasMore || loading}
          >
            Next →
          </button>
        </div>
      )}

      {/* Modal */}
      {selectedMember && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {selectedMember.MemName}'s Details
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                  aria-label="Close"
                />
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-4 text-center">
                    <img
                      src={getImage(selectedMember)}
                      alt={selectedMember.MemName}
                      className="rounded-circle mb-3"
                      style={{
                        width: "150px",
                        height: "150px",
                        objectFit: "cover",
                        border: "3px solid #007bff",
                      }}
                    />
                    <h5 className="fw-bold">{selectedMember.MemName}</h5>
                    <span className="badge bg-primary mb-2">
                      {selectedMember.MemType} Member
                    </span>
                    <p className="text-muted small mb-1">
                      <strong>Member ID:</strong> {selectedMember.MemMSNO}
                    </p>
                    <p className="text-muted small mb-1">
                      <strong>Phone:</strong> {selectedMember.MemMobileNo}
                    </p>
                    <p className="text-muted small mb-1">
                      <strong>Email:</strong> {selectedMember.MemEmailID}
                    </p>
                    <div className="card mt-3">
                      <div className="card-header">Occupations</div>
                      <ul className="list-group list-group-flush">
                        {selectedMember.Occupations.map((job, index) => (
                          <li key={index} className="list-group-item">
                            {job.CompName || "N/A"}
                            <br />
                            <small>{job.NatureOfBusiness || "N/A"}</small>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="col-md-8">
                    <h5 className="mb-3">Family / Dependent Information</h5>
                    {relationLoading ? (
                      <p className="text-muted">Loading family details...</p>
                    ) : relationData.length > 0 ? (
                      <ul className="list-group">
                        {relationData.map((rel) => (
                          <li
                            key={rel.DependentID}
                            className="list-group-item d-flex align-items-center"
                          >
                            <img
                              src={getDependentImage(rel.Dep_ImageName)}
                              alt={rel.DependentName}
                              className="rounded-circle me-3"
                              style={{
                                width: "50px",
                                height: "50px",
                                objectFit: "cover",
                              }}
                            />
                            <div>
                              <strong>{rel.Name}</strong>
                              <div className="text-muted small">
                                {rel.Relation}
                              </div>
                              <div className="text-muted small">
                                {rel.MobileNo}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted">No family data available.</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
